if (!customElements.get('karigari-hero-slider')) {
  customElements.define(
    'karigari-hero-slider',
    class KarigariHeroSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.karigari-hero-banner__slide'));
        this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
        this.prevButton = this.querySelector('[data-hero-prev]');
        this.nextButton = this.querySelector('[data-hero-next]');
        this.current = 0;
        this.isAnimating = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (this.dataset.single === 'true' || this.slides.length < 2) return;

        this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
        this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
        this.pauseOnHover = this.dataset.pauseHover !== 'false';

        this.onPrev = () => this.goTo(this.current - 1, true);
        this.onNext = () => this.goTo(this.current + 1, true);
        this.onKeyDown = this.handleKeyDown.bind(this);
        this.onVisibility = this.handleVisibility.bind(this);

        if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const index = parseInt(dot.dataset.heroDot, 10);
            if (!Number.isNaN(index)) this.goTo(index, true);
          });
        });

        this.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('visibilitychange', this.onVisibility);

        if (this.pauseOnHover && this.autoplay) {
          this.onMouseEnter = () => this.pauseAutoplay(true);
          this.onMouseLeave = () => this.startAutoplay();
          this.addEventListener('mouseenter', this.onMouseEnter);
          this.addEventListener('mouseleave', this.onMouseLeave);
          this.addEventListener('focusin', this.onMouseEnter);
          this.addEventListener('focusout', this.onMouseLeave);
        }

        this.startAutoplay();
      }

      disconnectedCallback() {
        this.pauseAutoplay();
        if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
        this.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.onMouseEnter) {
          this.removeEventListener('mouseenter', this.onMouseEnter);
          this.removeEventListener('mouseleave', this.onMouseLeave);
          this.removeEventListener('focusin', this.onMouseEnter);
          this.removeEventListener('focusout', this.onMouseLeave);
        }
      }

      handleKeyDown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.current - 1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.current + 1, true);
        }
      }

      handleVisibility() {
        if (document.hidden) {
          this.pauseAutoplay();
        } else {
          this.startAutoplay();
        }
      }

      startAutoplay() {
        this.pauseAutoplay();
        if (!this.autoplay) return;

        this.classList.add('is-playing');
        this.restartDotProgress();

        this.autoplayTimer = window.setTimeout(() => {
          this.goTo(this.current + 1, false);
        }, this.speed);
      }

      pauseAutoplay(keepProgress) {
        if (this.autoplayTimer) {
          window.clearTimeout(this.autoplayTimer);
          this.autoplayTimer = null;
        }
        this.classList.remove('is-playing');
        if (!keepProgress) this.clearDotProgress();
      }

      restartDotProgress() {
        this.clearDotProgress();
        const activeDot = this.dots[this.current];
        if (!activeDot) return;
        const progress = activeDot.querySelector('.karigari-hero-banner__dot-progress');
        if (!progress) return;
        progress.style.animationDuration = `${this.speed}ms`;
        // Force restart CSS animation
        progress.classList.remove('is-running');
        void progress.offsetWidth;
        progress.classList.add('is-running');
      }

      clearDotProgress() {
        this.dots.forEach((dot) => {
          const progress = dot.querySelector('.karigari-hero-banner__dot-progress');
          if (progress) progress.classList.remove('is-running');
        });
      }

      goTo(index, userTriggered) {
        if (this.isAnimating || this.slides.length < 2) return;

        const total = this.slides.length;
        const next = ((index % total) + total) % total;
        if (next === this.current) {
          if (userTriggered) this.startAutoplay();
          return;
        }

        this.isAnimating = true;
        const currentSlide = this.slides[this.current];
        const nextSlide = this.slides[next];

        currentSlide.classList.remove('is-active');
        currentSlide.classList.add('is-leaving');
        currentSlide.setAttribute('aria-hidden', 'true');

        nextSlide.classList.add('is-active', 'is-entering');
        nextSlide.setAttribute('aria-hidden', 'false');

        this.dots.forEach((dot, i) => {
          const active = i === next;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        this.current = next;

        const transitionMs =
          parseFloat(getComputedStyle(this).getPropertyValue('--karigari-hero-transition')) * 1000 || 900;

        window.setTimeout(() => {
          currentSlide.classList.remove('is-leaving');
          nextSlide.classList.remove('is-entering');
          this.isAnimating = false;
          this.startAutoplay();
        }, transitionMs);
      }
    }
  );
}
